import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, Radio, Gavel, Award, Users, Globe2, ArrowRight } from "lucide-react";
import { STATUS_META, fmtMoney } from "@/lib/data";
import { useAuth } from "@/lib/AuthContext";
import TavilyUsageCard from "@/components/TavilyUsageCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, testId, delay }) => (
  <div className="bg-white border border-slate-200 p-6 fade-up" style={{ animationDelay: `${delay}s` }} data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <Icon className="w-4 h-4 text-[#009EDB]" aria-hidden="true" />
    </div>
    <p className="font-mono text-3xl font-semibold text-slate-900">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/tenders`).then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#0050A0] mb-3">Command Centre</p>
        <h1 className="font-serif text-4xl text-slate-900">Procurement Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <StatCard icon={FileText} label="Total Tenders" value={stats?.total_tenders ?? "—"} testId="stat-total-tenders" delay={0} />
        <StatCard icon={Radio} label="Published" value={stats?.published ?? "—"} testId="stat-published" delay={0.05} />
        <StatCard icon={Gavel} label="Under Evaluation" value={stats?.under_evaluation ?? "—"} testId="stat-under-evaluation" delay={0.1} />
        <StatCard icon={Award} label="Awarded" value={stats?.awarded ?? "—"} testId="stat-awarded" delay={0.15} />
        <StatCard icon={Users} label="Bids Received" value={stats?.total_bids ?? "—"} testId="stat-total-bids" delay={0.2} />
        <StatCard icon={Globe2} label="Countries" value={stats?.countries ?? "—"} testId="stat-countries" delay={0.25} />
        <div className="bg-[#0050A0] p-6 sm:col-span-2 fade-up" style={{ animationDelay: "0.3s" }} data-testid="stat-total-value">
          <span className="text-xs uppercase tracking-[0.2em] text-[#7DD3F5]">Total Procurement Value (est.)</span>
          <p className="font-mono text-3xl font-semibold text-white mt-4">
            {stats ? `$ ${Number(stats.total_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
          </p>
        </div>
      </div>

      {user.role === "admin" && <TavilyUsageCard />}

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-slate-900">Recent Tenders</h2>
        <Link to="/tenders" data-testid="dashboard-view-all-link" className="text-sm text-[#0050A0] font-medium inline-flex items-center gap-1 hover:text-[#009EDB] transition-colors duration-200">
          View all <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="bg-white border border-slate-200 p-16 text-center" data-testid="dashboard-empty-state">
          <p className="text-slate-500 mb-4">No tenders floated yet.</p>
          <Link to="/tenders/new" data-testid="dashboard-create-first-btn" className="inline-block bg-[#0050A0] text-white px-6 py-3 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
            Float your first tender
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {recent.map((t, i) => (
            <Link
              key={t.id}
              to={`/tenders/${t.id}`}
              data-testid={`recent-tender-row-${i}`}
              className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-5 hover:bg-slate-50 transition-colors duration-200"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="font-mono text-xs text-slate-400 shrink-0">{t.reference_number}</span>
                <span className="text-sm font-medium text-slate-900 truncate">{t.title}</span>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-xs text-slate-500">{t.country}</span>
                <span className="font-mono text-xs text-slate-600">{fmtMoney(t.estimated_value, t.currency)}</span>
                <span className={`text-xs px-2.5 py-1 border ${STATUS_META[t.status]?.cls}`}>{STATUS_META[t.status]?.label}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
